import React, { useState } from 'react';
import SafeIcon from '../common/SafeIcon';
import { parseVideotapClipLink } from '../../utils/videotapClip';

/**
 * Fills clip title/start/end from a Videotap markdown link on the clipboard
 * (or a small paste field if reading the clipboard isn't possible / parse fails).
 */
export default function PasteClipButton({
  disabled = false,
  onParsed,
  className = '',
}) {
  const [showFallback, setShowFallback] = useState(false);
  const [fallbackValue, setFallbackValue] = useState('');
  const [error, setError] = useState('');
  const [busy, setBusy] = useState(false);

  const applyParsed = (raw) => {
    const parsed = parseVideotapClipLink(raw);
    if (!parsed) {
      setError('No se pudo leer el formato del clip');
      return false;
    }
    setError('');
    setShowFallback(false);
    setFallbackValue('');
    onParsed?.(parsed);
    return true;
  };

  const openFallback = (prefill = '', message = '') => {
    setShowFallback(true);
    setFallbackValue(prefill);
    if (message) setError(message);
  };

  const handlePasteClick = async () => {
    setError('');
    setBusy(true);
    try {
      if (navigator.clipboard?.readText) {
        const text = await navigator.clipboard.readText();
        if (text?.trim()) {
          if (applyParsed(text)) return;
          // Show what was received so the user can fix/retry
          openFallback(text.trim(), 'No se pudo leer el formato del clip');
          return;
        }
      }
      openFallback('');
    } catch {
      openFallback('');
    } finally {
      setBusy(false);
    }
  };

  const handleFallbackApply = () => {
    applyParsed(fallbackValue);
  };

  return (
    <div className={className}>
      <button
        type="button"
        onClick={handlePasteClick}
        disabled={disabled || busy}
        className="inline-flex items-center gap-1.5 text-[10px] font-black uppercase tracking-widest text-brand-cyan hover:text-brand-green disabled:opacity-50"
      >
        {busy ? (
          <SafeIcon name="Loader" className="w-3.5 h-3.5 animate-spin" />
        ) : (
          <SafeIcon name="Clipboard" className="w-3.5 h-3.5" />
        )}
        Pegar Clip
      </button>

      {showFallback && (
        <div className="mt-3 space-y-2">
          <p className="text-xs text-[var(--text-secondary)]">
            Pega el enlace de Videotap aquí:
          </p>
          <textarea
            value={fallbackValue}
            onChange={(e) => setFallbackValue(e.target.value)}
            disabled={disabled}
            rows={2}
            placeholder="[Título00:00 - 00:00](https://videotap.com/...)"
            className="w-full bg-[var(--bg-base)] border border-[var(--border-subtle)] rounded-xl py-2 px-3 text-xs text-[var(--text-main)] placeholder-[var(--text-secondary)]/50 focus:outline-none focus:border-brand-green resize-y"
          />
          <div className="flex gap-2">
            <button
              type="button"
              onClick={handleFallbackApply}
              disabled={disabled || !fallbackValue.trim()}
              className="px-3 py-1.5 rounded-full gfi-gradient-bg text-brand-navy text-[10px] font-black uppercase tracking-widest disabled:opacity-50"
            >
              Aplicar
            </button>
            <button
              type="button"
              onClick={() => {
                setShowFallback(false);
                setFallbackValue('');
                setError('');
              }}
              disabled={disabled}
              className="px-3 py-1.5 text-[10px] font-black uppercase tracking-widest text-[var(--text-secondary)]"
            >
              Cancelar
            </button>
          </div>
        </div>
      )}

      {error && (
        <p className="mt-2 text-xs text-red-300">{error}</p>
      )}
    </div>
  );
}
